import { Injectable, OnDestroy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Employee } from '../model/employee';
import { Menu } from '../model/login-response.dto';
import { Reservation, ReservationPurpose, ReservationStatus, Shift, ShiftType, Table, TableSlot } from '../model/reservation';
import { Source } from '../model/source';
import * as EmployeeSelectors from '../store/selectors/employee.selectors';
import * as ReservationListSelectors from '../store/selectors/reservation-list.selectors';
import * as AppSelectors from '../store/selectors/app.selectors';
import { LocalStorageService } from './local-storage.service';
import { UtilsService } from './utils.service';
import { ReservationTag, ReservationTagCategory } from '../model/reservation-tag';
import { Common } from './common-methods';
import { ReservationType } from '@model/tables-plan';

export type DayPart = {
  hours?: number,
  minsFrom?: number,
  minsTo?: number,
  label?: string,
  reservations?: Reservation[],
  totalCovers?: number,
  totalRes?: number,

  timeFrom?: number;
  timeTo?: number;
}

const arrivalTimeNotificationMinutes: number = 5;

@Injectable({
  providedIn: 'root'
})
export class ReservationService implements OnDestroy {

  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private store: Store<{ employee: Employee[] }>,
    private localStorageService: LocalStorageService,
    private utilsService: UtilsService,
    private translate: TranslateService) {

    this.employee$.pipe(takeUntil(this.destroy$)).subscribe((employee) => {
      this.employee = employee;
    });

    this.menus$.pipe(takeUntil(this.destroy$)).subscribe((menus) => {
      this.menus = menus;
    });

    this.extraStatuses$.pipe(takeUntil(this.destroy$)).subscribe((extraStatuses) => {
      this.extraStatuses = extraStatuses;
    });

    this.tags$.pipe(takeUntil(this.destroy$)).subscribe((tagCategories) => {
      if (tagCategories == null) {
        return;
      }

      this.tagCategoryIdToOrderDictionary = {};
      for (let tagCategory of tagCategories) {
        this.tagCategoryIdToOrderDictionary[tagCategory.id] = tagCategory.order ?? 0;
      }
    });

    this.minTelephoneLength$.pipe(takeUntil(this.destroy$)).subscribe((minTelephoneLength) => {
      this.minTelephoneLength = minTelephoneLength;
    });
  }

  employee$: Observable<Employee> = this.store.pipe(select(EmployeeSelectors.selectLoggedInemployee));
  employee: Employee = new Employee();
  pendingReservations$: Observable<Reservation[]> = this.store.pipe(select(ReservationListSelectors.selectPendingReservations));
  menus$: Observable<Menu[]> = this.store.pipe(select(AppSelectors.selectMenus));
  menus: Menu[] = [];
  tags$: Observable<ReservationTagCategory[] | null> = this.store.pipe(select(AppSelectors.selectTags));
  tagCategoryIdToOrderDictionary: { [id: string]: number; } = {};
  extraStatuses$: Observable<string[]> = this.store.pipe(select(AppSelectors.selectExtraStatuses));
  extraStatuses: string[] = [];
  minTelephoneLength$: Observable<number> = this.store.pipe(select(AppSelectors.selectMinTelephoneLength));
  minTelephoneLength: number = 0;

  noCountStatuses = ["Waiting", "Pending", "Cancelled", "NotShown", "WalkOut", "Rejected"];
  pendingStatuses = ["Confirmed", "LateBooking", "CheckedIn(Partial)"];

  private readonly WORKING_DAY_START_HOUR = 6;

  processShifts(shifts: Shift[], fullDayFirst?: boolean, includeInactive?: boolean) {

    if (shifts == null) {
      shifts = [];
    }
    else {
      
      const workingDayStartHour = this.WORKING_DAY_START_HOUR;

      shifts.sort(function (a, b) {

        const aFroHour = a.fromHour ?? 0;
        const bFroHour = b.fromHour ?? 0;

        var testA = aFroHour < workingDayStartHour ? aFroHour + 24 : aFroHour;
        var testB = bFroHour < workingDayStartHour ? bFroHour + 24 : bFroHour;

        return (testA > testB)
          ? 1
          : ((testB > testA) ? -1 : 0);
      });
    }

    var fullDayShift: Shift = {
      shiftId: 'ALL_DAY',
      type: ShiftType.AllDay,
      isActive: true,
      isFullDay: true,
      title: "All Day",
      fromHour: 6,
      fromMin: 0,
      toHour: 5,
      toMin: 59
    };

    if (fullDayFirst === true) {
      shifts.unshift(fullDayShift);
    }
    else {
      shifts.push(fullDayShift);
    }

    var filtered = [];

    for (let shift of shifts) {
      if (includeInactive !== true && shift.isActive == false) continue;

      filtered.push(shift);
    }

    return filtered;
  }

  createDayParts(
    interval: number,
    fromHour: number = 0,
    fromMin: number = 0,
    toHour: number = 23,
    toMin: number = 59,
    offsetHours: number = this.WORKING_DAY_START_HOUR
  ): DayPart[] {

    const parts: DayPart[] = [];

    let timeSpan = toHour * 60 + toMin - fromHour * 60 - fromMin; // minutes

    // when fromHour > toHour (toHour is after midnight)
    if (timeSpan < 0) {
      // take the complementary minutes
      timeSpan = 60 * 24 - (Math.abs(timeSpan) - 1);
    }

    const startTime = new Date(0, 0, 0, fromHour + offsetHours, fromMin, 0);
    let minutes = 0;

    while (minutes < timeSpan) {

      const timeFrom = moment(startTime).add(minutes, 'm').toDate();
      const timeTo = moment(timeFrom).add(interval - 1, 'm').toDate();

      const hoursFrom = timeFrom.getHours();
      const hoursTo = timeTo.getHours();
      const minsFrom = timeFrom.getMinutes();
      const minsTo = timeTo.getMinutes();

      const hoursFromFull = hoursFrom < 10 ? "0" + hoursFrom : hoursFrom;
      const hoursToFull = hoursTo < 10 ? "0" + hoursTo : hoursTo;
      const minsFromFull = minsFrom < 10 ? "0" + minsFrom : minsFrom;
      const minsToFull = minsTo < 10 ? "0" + minsTo : minsTo;

      parts.push({
        hours: hoursFrom,
        minsFrom,
        minsTo,
        label: `${hoursFromFull}:${minsFromFull} - ${hoursToFull}:${minsToFull}`,
        reservations: [],
        timeFrom: timeFrom.getTime(),
        timeTo: timeTo.getTime()
      });

      minutes += interval;
    }

    return parts;
  }

  assignReservationToDayPart(reservation: Reservation, allDayParts: DayPart[], purpose: ReservationPurpose) {

    try {

      if (this.noCountStatuses.some(status => reservation.reservationDetails.reservationsStatus === status)) {
        return;
      }

      if (purpose !== 'All' && reservation.reservationDetails.purpose !== purpose) {
        return;
      }

      const arrivalTime = moment(reservation.reservationDetails.arrivalTime).toDate();

      var resHours = arrivalTime.getHours();
      var resMins = arrivalTime.getMinutes();
      let targetDate = new Date(0, 0, 0, resHours, resMins);
      if (resHours < this.WORKING_DAY_START_HOUR) {
        targetDate = moment(targetDate).add(1, 'days').toDate();
      }
      const targetTimeStamp = targetDate.getTime();

      for (var i = 0; i < allDayParts.length; i++) {
        var part = allDayParts[i];

        //if (part.hours != resHours) continue;
        //if (resMins >= (part.minsFrom ?? 0) && resMins <= (part.minsTo ?? 0)) {
        if (targetTimeStamp >= part.timeFrom! && targetTimeStamp < part.timeTo!) {
          if (part.reservations == null) {
            part.reservations = [];
          }

          part.reservations.push(reservation);
          break;
        }
      }
    }
    catch (e) {
      console.error("Could not assign reservation to day part", e);
    }

  }

  calculateBlocking(reservations: Reservation[], purpose: ReservationPurpose): {
    totalCovers: number,
    totalRes: number,
    pendingRes: number,
    pendingCovers: number
  } {
    let totalRes = 0;
    let totalCovers = 0;
    let pendingRes = 0;
    let pendingCovers = 0;

    for (const reservation of reservations) {

      if (this.noCountStatuses.some(status => reservation.reservationDetails.reservationsStatus === status)) {
        continue;
      }

      if (purpose !== 'All' && reservation.reservationDetails.purpose !== purpose) {
        continue;
      }

      totalRes++;
      totalCovers += reservation.reservationDetails.covers;

      if (!this.pendingStatuses.some(status => reservation.reservationDetails.reservationsStatus === status)) {
        continue;
      }

      pendingRes++;
      pendingCovers += reservation.reservationDetails.covers;
    }

    return {
      totalCovers: totalCovers,
      totalRes: totalRes,
      pendingRes: pendingRes,
      pendingCovers: pendingCovers
    }
  }

  hourPartBelongsToShift(shift: Shift, hour: number | undefined, minFrom: number | undefined, minTo: number | undefined) {

    if (hour == null || minFrom == null || minTo == null) {
      return false;
    }

    if (shift == null) {
      return true;
    }

    if (shift.isFullDay === true) {
      return true;
    }

    const shiftFromHour = shift.fromHour ?? 0;
    const shiftToHour = shift.toHour ?? 0;
    const shiftFromMin = shift.fromMin ?? 0;
    const shiftToMin = shift.toMin ?? 0;

    var shiftChangesDay = shiftFromHour > shiftToHour;
    var hourIsAfterMidnight = hour < this.WORKING_DAY_START_HOUR;

    if (shiftChangesDay === true) {
      if (hourIsAfterMidnight === true) {
        if (hour > shiftToHour) return false;

        if (hour == shift.toHour && minTo > shiftToMin) return false;
      }
      else {
        if (hour < shiftFromHour) return false;

        if (hour == shiftFromHour && minFrom < shiftFromMin) return false;
      }
    }
    else {
      if (hour < shiftFromHour || hour > shiftToHour) return false;

      if (hour == shiftFromHour && minFrom < shiftFromMin) return false;

      if (hour == shift.toHour && minTo > shiftToMin) return false;
    }

    return true;
  }

  prepareForSave(reservation: Reservation) {
    let isNew = reservation.reservationDetails.reservationId == null;
    const time = new Date();

    reservation.timePerformed = moment(time).format("YYYY-MM-DD HH:mm:SS.sss");
    reservation.typeOfModification = isNew === true ? "CREATED" : "EDITED";
    reservation.modifiedBy = this.employee.surname;

    // use bound values to update customer details
    // since we want them to be aligned
    reservation.customerDetails.smokingArea = reservation.reservationDetails.smokingArea;
    reservation.customerDetails.customerType = reservation.reservationDetails.reservationType;

    // Always send NULL customerId so that we force back end to match customer by other fields
    // since it is a bit safer when user edits a reservation and changes user details            
    if (reservation.customerDetails.customerId === '0') {
      reservation.customerDetails.customerId = null;
    }

    // Always true since version that supports sources!
    reservation.hasSource = true;

    const statusesWithoutConfirmedBy = ["Pending", "Waiting", "NotConfirmed"];

    if (isNew === true) {
      const timestamp = time.getTime();

      reservation.reservationDetails.createdBy = this.employee.surname;
      reservation.reservationDetails.creationTime = timestamp;
      reservation.reservationDetails.pocket = true;
      reservation.reservationDetails.channel = "ihost_pocket_app";
      reservation.customerDetails.creationTime = timestamp;
      reservation.reservationDetails.tmpId = undefined;

      if (statusesWithoutConfirmedBy.indexOf(reservation.reservationDetails.reservationsStatus) == -1) {
        reservation.reservationDetails.confirmedBy = this.employee.surname;
      }

      if (reservation.reservationDetails.reservationsStatus == "WalkIn" ||
        reservation.reservationDetails.reservationsStatus == "Waiting") {
        reservation.reservationDetails.walkIn = true;
      }
      else if (reservation.reservationDetails.reservationsStatus == "LateBooking") {
        reservation.reservationDetails.lateBooking = true;
      }
    }
    else {
      if (statusesWithoutConfirmedBy.indexOf(reservation.reservationDetails.reservationsStatus) > -1) {
        reservation.reservationDetails.confirmedBy = null;
      }
      else if (reservation.reservationDetails.confirmedBy == null || reservation.reservationDetails.confirmedBy.trim() == "") {
        reservation.reservationDetails.confirmedBy = this.employee.surname;
      }
    }

    if (this.localStorageService.areMenusAvailable() === true) {
      if (reservation.reservationDetails.selectedMenu == null) {
        reservation.reservationDetails.selectedMenu = "-";
      }

      reservation.reservationDetails.fixMenu = reservation.reservationDetails.selectedMenu != "-";
    }
    else {
      reservation.reservationDetails.selectedMenu = null;
    }

    //remove online category
    const resOnlineCategory = reservation.reservationDetails.onlineCategory;

    if (resOnlineCategory != null && resOnlineCategory.id == null)
      reservation.reservationDetails.onlineCategory = null;

    // trim discount
    if (reservation.reservationDetails.discount != null &&
      (reservation.reservationDetails.discount as string).trim && (reservation.reservationDetails.discount as string).trim() == "") {
      reservation.reservationDetails.discount = 0;
    }

    // trim surname
    if (reservation.customerDetails.surname != null && reservation.customerDetails.surname.trim) {
      reservation.customerDetails.surname = reservation.customerDetails.surname.trim();
    }

    // trim name
    if (reservation.customerDetails.name != null && reservation.customerDetails.name.trim) {
      reservation.customerDetails.name = reservation.customerDetails.name.trim();
    }

    // trim mail
    if (reservation.customerDetails.mail != null && reservation.customerDetails.mail.trim) {
      reservation.customerDetails.mail = reservation.customerDetails.mail.trim();
    }

    // trim allergy
    if (reservation.reservationDetails.allergy != null && reservation.reservationDetails.allergy.trim) {
      reservation.reservationDetails.allergy = reservation.reservationDetails.allergy.trim();
    }

    // trim notes
    if (reservation.reservationDetails.notes != null && reservation.reservationDetails.notes.trim) {
      reservation.reservationDetails.notes = reservation.reservationDetails.notes.trim();
    }

    const reservationHasSource = reservation.sourceDetails != null &&
      reservation.sourceDetails.id != null &&
      reservation.sourceDetails.id != "";

    if (reservationHasSource === false) {
      reservation.sourceDetails = null;
      reservation.sourceCategory = null;
      reservation.sourceType = null;
      reservation.sourceTypeOfVisit = null;
    }
    else {
      const isExistingSource = reservation.sourceDetails?.id != "NEW";

      if (isExistingSource == false) {
        var newSource: Source = {
          companyId: null,
          companyTitle: reservation.sourceDetails?.companyTitle,
          contactId: null,
          contactMail: reservation.sourceDetails?.contactMail,
          contactName: reservation.sourceDetails?.contactName,
          contactSurname: reservation.sourceDetails?.contactSurname,
          contactTelephone: reservation.sourceDetails?.contactTelephone,
          descriptionEl: null,
          descriptionEn: null,
          id: null,
          order: null,
          sourceCategory: "-",
          sourceType: "REFERENCE",
          subObjectList: null,
          timeperformed: null,
          typeOfVisit: "RESERVATION"
        }

        reservation.sourceDetails = newSource;
      }

      reservation.sourceTypeOfVisit = reservation.sourceDetails?.typeOfVisit;
      reservation.sourceCategory = reservation.sourceDetails?.sourceCategory;
      reservation.sourceType = reservation.sourceDetails?.sourceType;

      if (reservation.sourceDetails != null) {
        delete reservation.sourceDetails.$$hashKey;
      }
    }

    this.cleanUpExtraneousFields(reservation);

    // uncomment this for simulating bad request
    //delete reservation.reservationDetails.arrivalTime;
  }

  cleanUpExtraneousFields(reservation: Reservation) {
    if (reservation.reservationDetails?.extraInfo != null) {
      reservation.reservationDetails.extraInfo = undefined;
    }

    if (reservation.reservationDetails?.onlineCategory?.nameLocalized != null) {
      reservation.reservationDetails.onlineCategory.nameLocalized = undefined;
    }
  }

  translateShiftTitle(shiftType: ShiftType | undefined) {
    switch (shiftType) {
      case ShiftType.Morning:
        return this.translate.instant('shifts.morning');
      case ShiftType.Afternoon:
        return this.translate.instant('shifts.afternoon');
      case ShiftType.Night:
        return this.translate.instant('shifts.night');
      case ShiftType.Midnight:
        return this.translate.instant('shifts.midnight');
      case ShiftType.AllDay:
        return this.translate.instant('shifts.allDay');
      default:
        return '';
    }
  }

  reservationHasSource(reservation: Reservation) {
    const src = reservation.sourceDetails;
    return src != null && (src.id != null && src.id.trim() != "");
  }

  reservationIsValid(reservation: Reservation, arrivalTime: { hours: number, mins: number } | null): { title: string, template: string } | null {

    const lang = this.translate.currentLang;
    const arrivalDate = moment(reservation.reservationDetails?.arrivalTime).toDate();
    const hasTel = reservation.customerDetails.telephone != null && reservation.customerDetails.telephone.trim() != "";
    const hasSurname = reservation.customerDetails.surname != null && reservation.customerDetails.surname.trim() != "";
    const isNewReservation = reservation.reservationDetails.reservationId == null;
    const hasEmail = reservation.customerDetails.mail != null && reservation.customerDetails.mail.trim() != "";
    const hasCovers = reservation.reservationDetails.covers != null && reservation.reservationDetails.covers > 0;
    const hasSource = this.reservationHasSource(reservation);
    const hasDate = moment(reservation.reservationDetails?.arrivalTime).isValid();
    const hasTime = hasDate && arrivalTime?.hours != null && arrivalTime.mins != null;
    const hasCustomerTags = (reservation.customerDetails?.customerTags ?? []).length > 0 || !Common.isNullOrEmpty(reservation.customerDetails?.allergy);
    const discountOk = reservation.reservationDetails?.discount == null ||
      (parseFloat(reservation.reservationDetails.discount.toString()) <= 100 &&
        parseFloat(reservation.reservationDetails.discount.toString()) >= 0);

    const billOK = reservation.reservationDetails.bill == null ||
      reservation.reservationDetails.bill.toString() == "" ||
      isNaN(reservation.reservationDetails.bill) === false

    if (hasCovers == false) {
      return {
        title: this.translate.instant('reservations.validationMessages.missingData'),
        template: this.translate.instant('reservations.validationMessages.pleaseSetCovers')
      };
    }

    if (hasDate == false) {
      return {
        title: this.translate.instant('reservations.validationMessages.missingData'),
        template: this.translate.instant('reservations.validationMessages.pleaseSetDate')
      };
    }

    if (hasTime == false) {
      return {
        title: this.translate.instant('reservations.validationMessages.missingData'),
        template: this.translate.instant('reservations.validationMessages.pleaseSetTime')
      };
    }

    // validate customer
    if (hasTel && !this.phoneIsValid(reservation.customerDetails.telephone)) {
      return {
        title: this.translate.instant('reservations.validationMessages.wrongData'),
        template: this.translate.instant('reservations.customerDetails.errorMessage.pleaseFillValidPhone')
      };
    }

    if (reservation.customerDetails.mail != null && reservation.customerDetails.mail.length > 0 && !this.mailIsValid(reservation.customerDetails.mail)) {
      return {
        title: this.translate.instant('reservations.validationMessages.wrongData'),
        template: this.translate.instant('reservations.customerDetails.errorMessage.pleaseFillValidEmail')
      };
    }

    if (hasTel === true && hasSurname !== true) {
      return {
        title: this.translate.instant('reservations.validationMessages.wrongData'),
        template: this.translate.instant('reservations.customerDetails.errorMessage.pleaseFillSurnameWithPhone')
      };
    }
    // validate customer end

    if (isNewReservation && hasSurname === false && this.utilsService.canWrite('ValidateSurname')) {
      return {
        title: this.translate.instant('reservations.validationMessages.missingData'),
        template: this.translate.instant('reservations.validationMessages.pleaseInsertSurname')
      };
    }

    if (isNewReservation && hasSource === false && this.utilsService.canWrite('ValidateSource')) {
      return {
        title: this.translate.instant('reservations.validationMessages.missingData'),
        template: this.translate.instant('reservations.validationMessages.pleaseInsertSource')
      };
    }

    if (discountOk == false) {
      return {
        title: this.translate.instant('reservations.validationMessages.wrongData'),
        template: this.translate.instant('reservations.validationMessages.discountMustBeBetween')
      };
    }

    if (billOK == false) {
      return {
        title: this.translate.instant('reservations.validationMessages.wrongData'),
        template: this.translate.instant('reservations.validationMessages.billMustBeANumber')
      };
    }

    if (hasTel === true && hasSurname === false) {
      return {
        title: this.translate.instant('reservations.validationMessages.missingData'),
        template: this.translate.instant('reservations.validationMessages.pleaseInsertSurnameWithTelephone')
      };
    }

    if (hasTel === true && (reservation.customerDetails?.telephone ?? '').trim().length < this.minTelephoneLength) {
      return {
        title: this.translate.instant('reservations.validationMessages.wrongData'),
        template: this.translate.instant('reservations.validationMessages.telephoneNumberIsWrong')
      };
    }

    if (hasEmail === true && hasTel === false) {
      return {
        title: this.translate.instant('reservations.validationMessages.missingData'),
        template: this.translate.instant('reservations.validationMessages.pleaseInsertSurnameAndTelephoneWithMail')
      };
    }

    if (hasEmail === true && this.utilsService.mailIsValid(reservation.customerDetails?.mail) === false) {
      return {
        title: this.translate.instant('reservations.validationMessages.wrongData'),
        template: this.translate.instant('reservations.validationMessages.mailIsWrong')
      };
    }

    if (hasCustomerTags === true && (hasTel != true || hasSurname != true)) {
      return {
        title: this.translate.instant('reservations.validationMessages.missingData'),
        template: this.translate.instant('reservations.validationMessages.pleaseInsertSurnameAndTelephoneWithTags')
      };
    }

    if (this.isReturnTimeBeforeArrivalTime(reservation)) {
      return {
        title: this.translate.instant('reservations.validationMessages.wrongData'),
        template: this.translate.instant('reservations.validationMessages.returnTimeMustBeAfterArrivalTime')
      };
    }

    return null;
  }

  walkinIsValid(reservation: Reservation): { title: string, template: string } | null {

    const lang = this.translate.currentLang;

    if (reservation.reservationDetails.covers == null || reservation.reservationDetails.covers <= 0) {
      return {
        title: this.translate.instant('reservations.validationMessages.missingData'),
        template: this.translate.instant('reservations.validationMessages.pleaseSetCovers')
      };
    }

    return null;
  }

  isReturnTimeBeforeArrivalTime(reservation: Reservation) {
    const arrivalTime = moment(reservation.reservationDetails.arrivalTime);
    const returnTime = moment(reservation.reservationDetails.returnTime);

    return arrivalTime.isValid() && returnTime.isValid() && returnTime.isBefore(arrivalTime);
  }

  getEmptyReservation(): any {
    return {
      "modifiedBy": 0,
      "typeOfModification": null,
      "timePerformed": null,
      "isConflict": false,
      "reservationDetails": {
        "userId": null,
        "reservationId": null,
        "creationTime": null,
        "walkIn": false,
        "lateBooking": false,
        "arrivalTime": null,
        "duration": 120,
        "covers": 0,
        "purpose": "Food",
        "allocatedTables": null,
        "roomNumber": null,
        "roomBookingId": null,
        "reservationType": "S",
        "returnTime": null,
        "allergy": null,
        "childrerChairs": 0,
        "event": null,
        "channel": "Restaurant",
        "dislike": null,
        "like": null,
        "fixMenu": false,
        "selectedMenu": null,
        "smokingArea": false,
        "paymentType": "Cash",
        "notes": null,
        "post": false,
        "positiveComment": false,
        "negativeComment": false,
        "customerComment": null,
        "createdBy": null,
        "confirmedBy": null,
        "expiredArrivalTime": false,
        "expiredReturnTime": false,
        "bill": null,
        "historyChecked": false,
        "discount": 0,
        "hasCake": false,
        "online": false,
        "pocket": false,
        "availabilityId": null,
        "reservationsStatus": "NotConfirmed"
      },
      "customerDetails": {
        "customerId": null,
        "surname": null,
        "name": null,
        "customerType": "S",
        "telephone": null,
        "homePhone": null,
        "officePhone": null,
        "mail": null,
        "officeMail": null,
        "birthDay": null,
        "tourist": null,
        "smokingArea": false,
        "spender": null,
        "notes": null,
        "dislike": null,
        "like": null,
        "allergy": null,
        "favouriteTables": null,
        "hasBaby": null,
        "married": null,
        "showAs": null,
        "nationality": null,
        "country": null,
        "city": null,
        "region": null,
        "address": null,
        "zipcode": null,
        "gender": null,
        "creationTime": null,
        "group_of_cust": null,
        "cust_job": null,
        "cust_company": null,
        "company_vat": null,
        "last_visit": null,
        "before_last_visit": null,
        "reservations_counter": null,
        "cancelled_counter": null,
        "notshown_counter": null
      }
    }
  }

  getStatusesForNewReservations() {
    return ["Pending", "Waiting", "NotConfirmed", "Confirmed", "LateBooking", "WalkIn"];
  }

  getStatusTransitions(status: ReservationStatus | undefined): ReservationStatus[] {
    if (status == undefined) {
      return [];
    }

    const trans: { [id: string]: ReservationStatus[]; } = {
      "Pending": ["NotConfirmed", "Confirmed", "LateBooking", "Rejected"],
      "Waiting": ["WalkIn", "Rejected"],
      "NotConfirmed": ["Confirmed", "LateBooking", "Cancelled", "NotShown"],
      "Confirmed": ["CheckedIn(Partial)", "CheckedIn", "Cancelled", "NotShown"],
      "LateBooking": ["CheckedIn(Partial)", "CheckedIn", "Cancelled", "NotShown"],
      "CheckedIn(Partial)": ["CheckedIn", "Dessert", "Payment", "Prepare", "CheckedOut"],
      "CheckedIn": ["Dessert", "Payment", "Prepare", "CheckedOut"],
      "WalkIn": ["Dessert", "Payment", "Prepare", "CheckedOut"],
      "CheckedOut": ["CheckedIn(Partial)", "CheckedIn", "WalkIn"],
      "Cancelled": ["NotConfirmed", "Confirmed", "LateBooking", "NotShown"],
      "NotShown": ["NotConfirmed", "Confirmed", "LateBooking", "Cancelled"],
      "Rejected": ["Pending", "Waiting"],
      "Dessert": ["Payment", "Prepare", "CheckedOut"],
      "Payment": ["Prepare", "CheckedOut"],
      "Prepare": ["CheckedOut"]
    }

    const statusTransitions = trans[status];

    const removeStatus = function (val: ReservationStatus) {
      const index = statusTransitions.indexOf(val);

      if (index == -1) return;

      statusTransitions.splice(index, 1);
    }

    const extraStatuses = this.localStorageService.getExtraStatuses();
    const hasDessert = extraStatuses.indexOf("Dessert") > -1;
    const hasPayment = extraStatuses.indexOf("Payment") > -1;
    const hasPrepare = extraStatuses.indexOf("Prepare") > -1;

    if (hasDessert === false) {
      removeStatus("Dessert");
    }

    if (hasPayment === false) {
      removeStatus("Payment");
    }

    if (hasPrepare === false) {
      removeStatus("Prepare");
    }

    return statusTransitions;
  }

  getSourceCaption(src: Source | null, short: boolean) {
    if (src == null) return "";

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "WEBSITE") {
      return "Website";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "FACEBOOK") {
      return "Facebook";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "MHOSPITALITY") {
      return "m-hospitality";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "GOOGLE") {
      return "Google";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "INSTAGRAM") {
      return "Instagram";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "TRIP_ADVISOR") {
      return "Tripadvisor";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "RESY") {
      return "RESY";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "MICHELIN") {
      return "Michelin Guide";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "AROUNDTHETABLE") {
      return "Around The Table";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "TRIPAROUND") {
      return "Triparound";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "THELIST") {
      return "The List";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "TOPTOWN") {
      return "Top Town";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "DINEATHENS") {
      return "Dine Athens";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "VIP1") {
      return "VIP";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "PARTNERS1") {
      return "Partner";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "TIKTOK") {
      return "TikTok";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "TOURMIE") {
      return "Tourmie";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "EXCLUSIVI") {
      return "Exclusivi";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "INHOUSE") {
      return "In-House";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "GUESTS") {
      return "Guest";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "INFOTOUCH") {
      return "Infotouch";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "CAMPAIGN") {
      return "Campaign";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "MEMBERS") {
      return "Members";
    }

    if (src.sourceCategory == "EB2CPLATFORM" && src.sourceType == "GLAMBERS") {
      return "Glambers";
    }

    // unknown sources
    if (src.sourceCategory == "EB2CPLATFORM" && !Common.isNullOrEmpty(src.sourceType)) {
      return Common.capitalizeFirstLetter(src.sourceType) as string;
    }

    var arr = [];

    if (src.companyTitle != null && src.companyTitle.trim() != "") {
      arr.push(src.companyTitle);
    }

    if (src.contactSurname != null && src.contactSurname.trim() != "") {
      arr.push(src.contactSurname);
    }

    if (arr.length < 2 && src.contactName != null && src.contactName.trim() != "") {
      arr.push(src.contactName);
    }

    if (arr.length < 2 && src.contactTelephone != null && src.contactTelephone.trim() != "") {
      arr.push(src.contactTelephone);
    }

    if (arr.length < 2 && src.contactMail != null && src.contactMail.trim() != "") {
      arr.push(src.contactMail);
    }

    if (arr.length == 0) {
      arr.push(short === true ? "" : "-");
    }


    return short === true ? arr[0] : arr.join(", ");
  }

  selectedMenuToExtraInfo(selectedMenu: string | null | undefined) {
    if (selectedMenu == null || selectedMenu.trim() == '') {
      return '-';
    }

    const menu = this.menus.find(m => m.value === selectedMenu);

    if (menu != null) {

      if (this.translate.currentLang === 'el') {
        return menu.description_el;
      } else if (this.translate.currentLang === 'hr') {
        return menu.description_hr ?? menu.description_en;
      } else if (this.translate.currentLang === 'sr') {
        return menu.description_sr ?? menu.description_en;
      }

      return menu.description_en;
    }

    return '-';
  }

  initReservationList(reservations: Reservation[]) {
    return (reservations ?? []).map(r => this.initReservation(r));
  }

  initReservation(reservation: Reservation) {
    if (reservation.reservationDetails == null) {
      return reservation;
    }
    reservation.reservationDetails.extraInfo = this.selectedMenuToExtraInfo(reservation.reservationDetails?.selectedMenu);

    const expirationTime = this.isReservationExpiredTime(reservation);
    reservation.reservationDetails.expiredArrivalTime = expirationTime.arrivalTime;
    reservation.reservationDetails.expiredReturnTime = expirationTime.returnTime;

    return reservation;
  }

  hasDiscount(reservation: Reservation | undefined): boolean {
    if (reservation == null) {
      return false;
    }
    return reservation?.reservationDetails?.discount != null && (
      (typeof reservation.reservationDetails.discount == 'number' && reservation.reservationDetails.discount > 0)
      || (typeof reservation.reservationDetails.discount == 'string' && reservation.reservationDetails.discount.length > 0 && parseFloat(reservation.reservationDetails.discount) > 0)
    );
  }

  tagByCategoryOrderComperator(tagA: ReservationTag, tagB: ReservationTag): -1 | 0 | 1 {

    const tagCategotyAOrder = this.tagCategoryIdToOrderDictionary[tagA.categoryId];
    const tagCategotyBOrder = this.tagCategoryIdToOrderDictionary[tagB.categoryId];

    if (tagCategotyAOrder > tagCategotyBOrder) {
      return 1;
    }

    if (tagCategotyAOrder < tagCategotyBOrder) {
      return -1;
    }

    // necessary because some categories have the same order
    if (tagA.categoryId > tagB.categoryId) {
      return 1;
    }

    if (tagA.categoryId < tagB.categoryId) {
      return -1;
    }

    return Common.tagOrderComperator(tagA, tagB);
  }

  mailIsValid(email: string | null | undefined) {
    var re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email ?? '');
  }

  phoneIsValid(phone: string | null | undefined) {
    return phone != null && phone.trim().length >= this.minTelephoneLength;
  }

  getFilteredReservationStatuses() {
    const unsupportedStatusIds = Common.optionalStatusIds.filter(s => !this.extraStatuses.includes(s));
    return Common.reservationStatuses.filter(status => !unsupportedStatusIds.includes(status.id));
  }

  statusToType(status: ReservationStatus | undefined): ReservationType | null {

    switch (status) {
      case 'Waiting':
        return ReservationType.Waiting;
      case 'Pending':
        return ReservationType.Pending;
      case 'NotConfirmed':
      case 'Confirmed':
      case 'LateBooking':
      case 'CheckedIn(Partial)':
        return ReservationType.Booked;
      case 'CheckedIn':
      case 'WalkIn':
      case 'Dessert':
      case 'Payment':
      case 'Prepare':
        return ReservationType.Seated;
      case 'CheckedOut':
      case 'Cancelled':
      case 'NotShown':
      case 'Rejected':
        return ReservationType.Completed;
      default:
        return null;
    }

  }

  getCustomerName(reservation: Reservation | null): string {

    if (reservation == null) {
      return '';
    }

    const { surname, name } = reservation.customerDetails;

    let customerName = '';

    if (surname != null && name != null && !Common.isNullOrEmpty(surname) && !Common.isNullOrEmpty(name)) {
      customerName = `${surname} ${name[0]}.`;
    } else if (surname != null && !Common.isNullOrEmpty(surname)) {
      customerName = surname;
    } else if (name != null && !Common.isNullOrEmpty(name)) {
      customerName = name;
    } else if (reservation.reservationDetails.walkIn
      || reservation.reservationDetails.reservationsStatus === 'WalkIn'
      || reservation.reservationDetails.reservationsStatus === 'Waiting') {
      customerName = this.translate.instant('reservations.list.walkIn');
    }

    return customerName;
  }

  getSlotNumberForReservation(reservation: Reservation, isTheFirstReservation: boolean, shift: Shift) {

    const { fromHour, fromMin, toHour, toMin } = shift;

    const reservationArrivalDate = moment(reservation.reservationDetails.arrivalTime).toDate();

    // after midnight arrival time correction (select previous date)
    const isArrivalTimeNextDayMorning = Common.timeIsNextDayMorning(reservationArrivalDate);
    if (isArrivalTimeNextDayMorning) {
      reservationArrivalDate.setDate(reservationArrivalDate.getDate() - 1);
    }

    // Create from/to dates
    const from = this.adjustDateInWorkingDay(moment(reservationArrivalDate).set('hour', fromHour).set('minute', fromMin).toDate());
    const to = this.adjustDateInWorkingDay(moment(reservationArrivalDate).set('hour', toHour).set('minute', toMin).toDate());

    // Divide shift at 3 parts
    const shiftLength = to.getTime() - from.getTime();
    const step = shiftLength / 3;

    // Create dates for the end of first and second part so shift looks like this:
    // from.....firstBreakPoint.....secondBreakPoint.....to
    const firstBreakPoint = new Date(from.getTime() + step);
    const secondBreakPoint = new Date(from.getTime() + step * 2);

    const arrivalDate = moment(reservationArrivalDate);

    if ((isTheFirstReservation && arrivalDate.isBefore(from)) ||
      (arrivalDate.isSame(from)) ||
      (arrivalDate.isAfter(from) && arrivalDate.isBefore(firstBreakPoint)))
      return 1;
    else if (arrivalDate.isSame(firstBreakPoint) || arrivalDate.isBefore(secondBreakPoint))
      return 2;
    else
      return 3;

  }

  adjustDateInWorkingDay(date: Date) {
    if (date == null) {
      return date;
    }

    let rval = moment(date);
    const hour = rval.get('hour');

    if (hour >= 0 && hour <= Common.WORKING_DAY_START_HOUR) {
      rval = rval.add(1, 'days');
    }

    return rval.toDate();
  }

  isReservationExpiredTime(reservation: Reservation | null): { arrivalTime: boolean, returnTime: boolean } {
    const expiration = { arrivalTime: false, returnTime: false };

    if (reservation?.reservationDetails == null) {
      return expiration;
    }

    if (reservation.reservationDetails.reservationsStatus === 'Confirmed'
      || reservation.reservationDetails.reservationsStatus === 'NotConfirmed'
      || reservation.reservationDetails.reservationsStatus === 'LateBooking') {

      if (reservation.reservationDetails.arrivalTime != null
        && moment(reservation.reservationDetails.arrivalTime).isValid()
        && moment(reservation.reservationDetails.arrivalTime).add(arrivalTimeNotificationMinutes, 'minutes').isBefore(new Date())) {
        expiration.arrivalTime = true;
      }
    } else if (reservation.reservationDetails.returnTime != null
      && (reservation.reservationDetails.reservationsStatus === 'CheckedIn' ||
        reservation.reservationDetails.reservationsStatus === 'CheckedIn(Partial)' ||
        reservation.reservationDetails.reservationsStatus === 'WalkIn' ||
        reservation.reservationDetails.reservationsStatus === 'Dessert' ||
        reservation.reservationDetails.reservationsStatus === 'Payment' ||
        reservation.reservationDetails.reservationsStatus === 'Prepare')) {

      if (moment(reservation.reservationDetails.returnTime).isValid()
        && moment(reservation.reservationDetails.returnTime).isBefore(new Date())) {
        expiration.returnTime = true;
      }
    }

    return expiration;
  }

  getTableSlots(tableReservations: Reservation[] | null, shift: Shift | null | undefined): { slot1: TableSlot, slot2: TableSlot, slot3: TableSlot } {

    const slots: { slot1: TableSlot, slot2: TableSlot, slot3: TableSlot } = {
      slot1: { slotNumber: 1, reservation: null },
      slot2: { slotNumber: 2, reservation: null },
      slot3: { slotNumber: 3, reservation: null },
    };

    if (tableReservations == null || tableReservations.length <= 0 || shift == null) {
      return slots;
    }

    if (tableReservations.length >= 3) {
      slots.slot1.reservation = tableReservations[0];
      slots.slot2.reservation = tableReservations[1];
      slots.slot3.reservation = tableReservations[2];
      return slots;
    }

    const firstReservation = tableReservations[0];
    const secondReservation = tableReservations[1] ?? null;

    let pos1 = this.getSlotNumberForReservation(firstReservation, true, shift as Shift);
    let pos2 = secondReservation != null ? this.getSlotNumberForReservation(secondReservation, false, shift as Shift) : -1;

    // handle same position
    if (pos1 === pos2) {
      if (pos1 !== 3) {
        pos2++;
      }
      else {
        pos1--;
      }
    }

    this.setSlotReservation(slots, firstReservation, pos1);

    if (pos2 !== -1) {
      this.setSlotReservation(slots, secondReservation, pos2);
    }

    return slots;
  }

  getConflictingReservationsUsingDuration(reservation: Reservation | null, tableReservations: Reservation[] | null): Reservation[] {
    if (reservation == null || tableReservations == null || tableReservations.length <= 0) {
      return [];
    }

    const averageMilliseconds = reservation.reservationDetails.duration != null && reservation.reservationDetails.duration > 0
      ? reservation.reservationDetails.duration * 60 * 1000
      : 120 * 60 * 1000; // todo: get from settings

    const reservationArrivalTime = moment(reservation.reservationDetails.arrivalTime).valueOf();
    const reservationReturnTime = reservation.reservationDetails.returnTime != null && moment(reservation.reservationDetails.returnTime).isValid()
      ? moment(reservation.reservationDetails.returnTime).valueOf()
      : reservationArrivalTime + averageMilliseconds;

    const filterByStatusReservations = tableReservations.filter(tableReservation => {

      if (tableReservation?.reservationDetails == null) {
        return false;
      }

      if (tableReservation.reservationDetails.reservationId === reservation.reservationDetails.reservationId) {
        return false;
      }

      if (tableReservation.reservationDetails.reservationsStatus === 'Rejected'
        || tableReservation.reservationDetails.reservationsStatus === 'CheckedOut'
        || tableReservation.reservationDetails.reservationsStatus === 'Cancelled'
        || tableReservation.reservationDetails.reservationsStatus === 'Pending'
        || tableReservation.reservationDetails.reservationsStatus === 'NotShown') {
        return false;
      }

      return true;
    });

    const returnTimeConflictingReservations = filterByStatusReservations.filter(tableReservation => {
      if (tableReservation.reservationDetails.returnTime == null || !moment(reservation.reservationDetails.returnTime).isValid()) {
        return false;
      }

      const arrivalTime = moment(tableReservation.reservationDetails.arrivalTime).valueOf();
      const returnTime = moment(tableReservation.reservationDetails.returnTime).valueOf();

      return arrivalTime < reservationReturnTime && returnTime > reservationArrivalTime;
    });

    const noReturnTimeConflictingReservations = filterByStatusReservations.filter(tableReservation => {

      if (tableReservation.reservationDetails.returnTime != null && moment(reservation.reservationDetails.returnTime).isValid()) {
        return false;
      }

      const arrivalTime = moment(tableReservation.reservationDetails.arrivalTime).valueOf();

      return arrivalTime < reservationReturnTime && arrivalTime > (reservationArrivalTime - averageMilliseconds);
    });

    return returnTimeConflictingReservations.concat(noReturnTimeConflictingReservations);
  }

  getConflictingReservationsForTableAllocation(reservation: Reservation | null, reservations: Reservation[], allocatedTables: Table[]): Reservation[] {
    let conflictingReservations: Reservation[] = [];

    if (reservation == null) {
      return conflictingReservations;
    }

    for (let allocatedTable of allocatedTables) {

      const tableReservations = reservations
        .filter(r => Common.tableReservationsFilter(allocatedTable, r))
        .filter(r => Common.completedReservationsFilter(r))
        .sort(Common.arrivalTimeComparator);

      const tableConflictingReservations = this.getConflictingReservationsUsingDuration(reservation, tableReservations);

      conflictingReservations = conflictingReservations.concat(tableConflictingReservations);
    }

    conflictingReservations = conflictingReservations.filter(Common.onlyUnique);

    return conflictingReservations;
  }

  private setSlotReservation(slots: { slot1: TableSlot, slot2: TableSlot, slot3: TableSlot }, reservation: Reservation, slotNumber: number) {
    switch (slotNumber) {
      case 1:
        slots.slot1.reservation = reservation;
        break;
      case 2:
        slots.slot2.reservation = reservation;
        break;
      case 3:
        slots.slot3.reservation = reservation;
        break;
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
